import { and, eq, exists, inArray, sql, type SQL, type SQLWrapper } from "drizzle-orm";
import { aliasedTable } from "drizzle-orm/alias";
import { QueryBuilder, type AnyPgColumn, type AnyPgTable } from "drizzle-orm/pg-core";
import type { DbTransaction } from "../drizzle";

/**
 * Extended closure table support for DAG reachability and path counting.
 *
 * Reference:
 * https://lnagle.github.io/extended-closure-table-pattern.html
 *
 * Repo notes:
 * ../../docs/closure-table.md
 */

type NodeTableContract = AnyPgTable & { id: AnyPgColumn };

type EdgeTableContract = AnyPgTable & {
  sourceId: AnyPgColumn;
  destinationId: AnyPgColumn;
};

type ClosureTableContract = AnyPgTable & {
  ancestorId: AnyPgColumn;
  descendantId: AnyPgColumn;
  depth: AnyPgColumn;
  pathCount: AnyPgColumn;
};

type ClosureDelta = {
  ancestorId: string;
  descendantId: string;
  depth: number;
  pathCount: number;
};

type EdgeRef = { sourceId: string; destinationId: string };

export type DescendantHasAnyAncestorConditionArgs = {
  descendantId: string | SQLWrapper;
  ancestorIds?: readonly string[];
};

export type ClosureDagClient = {
  registerNode(tx: DbTransaction, args: { nodeId: string }): Promise<void>;
  addEdge(tx: DbTransaction, args: EdgeRef): Promise<void>;
  removeEdge(tx: DbTransaction, args: EdgeRef): Promise<void>;
  descendantHasAnyAncestor(
    tx: DbTransaction,
    args: { descendantId: string; ancestorIds?: readonly string[] },
  ): Promise<boolean>;
  descendantHasAnyAncestorCondition(args: DescendantHasAnyAncestorConditionArgs): SQL | undefined;
};

export function createClosureDagClient<
  TNodeTable extends NodeTableContract,
  TEdgeTable extends EdgeTableContract,
  TClosureTable extends ClosureTableContract,
>(args: {
  nodeTable: TNodeTable;
  edgeTable: TEdgeTable;
  closureTable: TClosureTable;
}): ClosureDagClient {
  const { nodeTable, edgeTable, closureTable } = args;
  const nodeTableAny = nodeTable as NodeTableContract;
  const edgeTableAny = edgeTable as EdgeTableContract;
  const closureTableAny = closureTable as ClosureTableContract;
  const qb = new QueryBuilder();

  function normalizeAncestorIds(ancestorIds?: readonly string[]) {
    const uniqueAncestorIds = Array.from(new Set(ancestorIds ?? []));
    return uniqueAncestorIds.length > 0 ? uniqueAncestorIds : undefined;
  }

  function descendantHasAnyAncestorCondition(
    args: DescendantHasAnyAncestorConditionArgs,
  ): SQL | undefined {
    const ancestorIds = normalizeAncestorIds(args.ancestorIds);
    if (!ancestorIds) {
      return undefined;
    }

    return exists(
      qb
        .select({ ancestorId: closureTableAny.ancestorId })
        .from(closureTableAny)
        .where(
          and(
            eq(closureTableAny.descendantId, args.descendantId),
            inArray(closureTableAny.ancestorId, ancestorIds),
          ),
        ),
    );
  }

  async function assertNodesExist(tx: DbTransaction, nodeIds: readonly string[]) {
    const uniqueNodeIds = Array.from(new Set(nodeIds));
    if (uniqueNodeIds.length === 0) {
      return;
    }

    const rows = await tx
      .select({ id: nodeTableAny.id })
      .from(nodeTableAny)
      .where(inArray(nodeTableAny.id, uniqueNodeIds));

    if (rows.length !== uniqueNodeIds.length) {
      const found = new Set(rows.map((row) => row.id as string));
      const missing = uniqueNodeIds.filter((id) => !found.has(id));
      throw new Error(`Nodes not found: ${missing.join(", ")}`);
    }
  }

  async function ensureSelfClosureRow(tx: DbTransaction, nodeId: string): Promise<void> {
    await tx
      .insert(closureTableAny)
      .values({
        ancestorId: nodeId,
        descendantId: nodeId,
        depth: 0,
        pathCount: 1,
      })
      .onConflictDoNothing();
  }

  async function computeClosureDeltas(
    tx: DbTransaction,
    sourceId: string,
    destinationId: string,
  ): Promise<ClosureDelta[]> {
    const leftClosure = aliasedTable(closureTableAny, "left_closure") as ClosureTableContract;
    const rightClosure = aliasedTable(closureTableAny, "right_closure") as ClosureTableContract;
    const depthSql = sql<number>`${leftClosure.depth} + ${rightClosure.depth} + 1`;
    const pathCountSql = sql<number>`sum(${leftClosure.pathCount} * ${rightClosure.pathCount})`;

    return (await tx
      .select({
        ancestorId: leftClosure.ancestorId,
        descendantId: rightClosure.descendantId,
        depth: depthSql,
        pathCount: pathCountSql,
      })
      .from(leftClosure)
      .innerJoin(rightClosure, eq(rightClosure.ancestorId, destinationId))
      .where(eq(leftClosure.descendantId, sourceId))
      .groupBy(leftClosure.ancestorId, rightClosure.descendantId, depthSql)
      .execute()) as ClosureDelta[];
  }

  async function applyClosureIncrements(tx: DbTransaction, deltas: readonly ClosureDelta[]) {
    for (const delta of deltas) {
      await tx
        .insert(closureTableAny)
        .values({
          ancestorId: delta.ancestorId,
          descendantId: delta.descendantId,
          depth: delta.depth,
          pathCount: delta.pathCount,
        })
        .onConflictDoUpdate({
          target: [closureTableAny.ancestorId, closureTableAny.descendantId, closureTableAny.depth],
          set: {
            pathCount: sql`${closureTableAny.pathCount} + ${delta.pathCount}`,
          },
        });
    }
  }

  async function applyClosureDecrements(tx: DbTransaction, deltas: readonly ClosureDelta[]) {
    for (const delta of deltas) {
      await tx
        .delete(closureTableAny)
        .where(
          and(
            eq(closureTableAny.ancestorId, delta.ancestorId),
            eq(closureTableAny.descendantId, delta.descendantId),
            eq(closureTableAny.depth, delta.depth),
            eq(closureTableAny.pathCount, delta.pathCount),
          ),
        );

      await (tx as any)
        .update(closureTableAny)
        .set({ pathCount: sql`${closureTableAny.pathCount} - ${delta.pathCount}` })
        .where(
          and(
            eq(closureTableAny.ancestorId, delta.ancestorId),
            eq(closureTableAny.descendantId, delta.descendantId),
            eq(closureTableAny.depth, delta.depth),
          ),
        );
    }
  }

  const closureDagClient: ClosureDagClient = {
    async registerNode(tx: DbTransaction, { nodeId }: { nodeId: string }): Promise<void> {
      await assertNodesExist(tx, [nodeId]);
      await ensureSelfClosureRow(tx, nodeId);
    },

    async addEdge(
      tx: DbTransaction,
      { sourceId, destinationId }: { sourceId: string; destinationId: string },
    ): Promise<void> {
      if (sourceId === destinationId) {
        throw new Error("Closure DAG edges cannot create self-loops");
      }

      await assertNodesExist(tx, [sourceId, destinationId]);
      await ensureSelfClosureRow(tx, sourceId);
      await ensureSelfClosureRow(tx, destinationId);

      const wouldCycle = await tx
        .select({ ancestorId: closureTable.ancestorId })
        .from(closureTableAny)
        .where(
          and(
            eq(closureTableAny.ancestorId, destinationId),
            eq(closureTableAny.descendantId, sourceId),
          ),
        )
        .limit(1);
      if (wouldCycle[0]) {
        throw new Error("Closure DAG edge would create a cycle");
      }

      const deltas = await computeClosureDeltas(tx, sourceId, destinationId);
      const insertedEdge = await tx
        .insert(edgeTableAny)
        .values({ sourceId, destinationId })
        .onConflictDoNothing()
        .returning({ sourceId: edgeTableAny.sourceId });
      if (insertedEdge.length === 0) {
        return;
      }

      await applyClosureIncrements(tx, deltas);
    },

    async removeEdge(
      tx: DbTransaction,
      { sourceId, destinationId }: { sourceId: string; destinationId: string },
    ): Promise<void> {
      const deletedEdge = await tx
        .delete(edgeTable)
        .where(
          and(eq(edgeTableAny.sourceId, sourceId), eq(edgeTableAny.destinationId, destinationId)),
        )
        .returning({ sourceId: edgeTableAny.sourceId });

      if (!deletedEdge[0]) {
        return;
      }

      const deltas = await computeClosureDeltas(tx, sourceId, destinationId);
      await applyClosureDecrements(tx, deltas);
    },

    async descendantHasAnyAncestor(
      tx: DbTransaction,
      args: {
        descendantId: string;
        ancestorIds?: readonly string[];
      },
    ): Promise<boolean> {
      const condition = descendantHasAnyAncestorCondition(args);
      if (!condition) {
        return false;
      }

      const [row] = await tx
        .select({ ancestorId: closureTableAny.ancestorId })
        .from(closureTableAny)
        .where(condition)
        .limit(1);
      return !!row;
    },

    descendantHasAnyAncestorCondition,
  };

  return closureDagClient;
}
